import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DataInputNavSVG from "../../assets/svgs/data-input-nav.svg";
import AddAccount from "../accounts-list/add-account/AddAccount";
import TempLogo from "../../assets/svgs/temp-logo.svg";
import styles from "./Header.module.scss";
import NavWrpr from "./nav/NavWrpr";
import { useNavigate, useLocation } from "react-router-dom";
import _ from "lodash";
import AccountSelection from "./AccountSelection";
import FacilitySelection from "./FacilitySelection";
import OverallSubSvg from "../../assets/svgs/OverallSubSvg.svg";
import HospitalSubSvg from "../../assets/svgs/HospitalSubSbg.svg";
import CommunityTransferSubSvg from "../../assets/svgs/CommunityTrasnferSubSvg.svg";
import DeceasedSubSvg from "../../assets/svgs/DeceasedSubSvg.svg";
import AdmissionsSubSvg from "../../assets/svgs/AdmissionsSubSvg.svg";
import classNames from "classnames";
import ExpandArrowSvg from "../../assets/svgs/ExpandArrow.svg";
import MenuIcon from "@mui/icons-material/Menu";
import PeopleIcon from "@mui/icons-material/People";
import { CircularProgress, Box, Typography } from "@mui/material";
import useTutorialTrigger from "../../hooks/useTutorialTrigger";
import { TUTORIAL_FEATURES } from "../../config/tutorials.config";

import { AUTH_ROLES } from "../../types/auth.type";
import {
  Avatar,
  IconButton,
  useMediaQuery,
  AppBar,
  Toolbar,
} from "@mui/material";
import ProfileMenu from "./ProfileMenu";
import { PAGE_TYPE } from "../../types/pages.type";
import { getPercentageAgainstAll } from "../../services/filter.service";
import { setCustomePercentages, setIsExpandedSideBar, setSelectedShortcutOptions } from "../../store/reducers/common.slice";
import { signOut } from "../../utilis/signout";
import {
  setAdmissionCards,
  setCommunityTransferCards,
  setDeceasedCards,
  setHospitalCards,
  setOverallCards,
} from "../../store/reducers/customCard.slice";
import { getCustomeCardFilters } from "../../services/custom-card.service";
import useSetParamToURL from "../hooks/useSetParamToURL";
import { getShortcutSetting } from "../../services/settings.service";
import { useContext } from "react";
import AppContext from "../../context/AppContext";
import { setExpandWidth, setMobileMenuOpen } from "../../store/reducers/ui.slice";
import MobileApp from "./MobileApp";
import useIsMobileDevice from "../../hooks/useIsMobileDevice";
import LogoutIcon from '@mui/icons-material/Logout';

const EXPAND_WIDTH_MD = "250px";
const NOT_EXPAND_WIDTH = "79px";
const LAPTOP_RESOLUTION = 1536;

const Header = (props) => {
  const { isLayoutMode } = props;
  const dispatch = useDispatch();
  const userOptionRef = useRef();
  const [showAddAccount, setShowAddAccount] = useState(false);
  // mobileMenuOpen is now managed in Redux
  const [hasShownInitialMobileMenu, setHasShownInitialMobileMenu] = useState(false);
  const [showMobileLoader, setShowMobileLoader] = useState(false);
  const isMd = useMediaQuery("(max-width:1280px)");
  const isMobile = useIsMobileDevice();

  let EXPAND_WIDTH = EXPAND_WIDTH_MD
  if (isMd) {
    EXPAND_WIDTH = "200px"
  }
  // Use expandWidth from Redux
  const expandWidth = useSelector(state => state.ui.expandWidth);
  // Use mobileMenuOpen from Redux
  const mobileMenuOpen = useSelector(state => state.ui.mobileMenuOpen);
  // Derive expanded from expandWidth - force collapsed when in layout mode
  const expanded = isLayoutMode ? false : (expandWidth !== NOT_EXPAND_WIDTH);

  const selectedFacilities = useSelector(
    (state) => state.activeFacilities?.facilities
  );
  const roles = {
    superAdmin: "Super admin",
    admin: "Admin",
    user: "User",
  };
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location?.pathname || "";
  const isMainMenuActive = pathname === "/data-input" || pathname === "/residence-list";
  const isDashboardActive = pathname.startsWith("/dashboard/");
  const { auth, account, permission } = useSelector(
    ({ auth, account, permission }) => ({
      auth,
      account,
      permission,
    })
  );
  const { selectedAccount } = useSelector((state) => state.common);
  const { timeLogControl } = useContext(AppContext);
  const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();

  useEffect(() => {
    dispatch(setIsExpandedSideBar(expanded));
    if (isMobile) {
      const root = document.querySelector(":root");
      root.style.setProperty("--sidebar-width", "0px");
    } else {
      // Update CSS root variable when layout mode changes
      if (isLayoutMode) {
        const root = document.querySelector(":root");
        root.style.setProperty("--sidebar-width", NOT_EXPAND_WIDTH);
      } else {
        const root = document.querySelector(":root");
        root.style.setProperty("--sidebar-width", expandWidth);
      }
    }
  }, [dispatch, expanded, isLayoutMode, expandWidth, isMobile]);

  // Auto-open mobile menu on first login
  useEffect(() => {
    if (isMobile && auth && !hasShownInitialMobileMenu && account && account.length > 0) {
      // Show mobile loader initially
      setShowMobileLoader(true);

      // Small delay to ensure everything is loaded
      const timer = setTimeout(() => {
        setShowMobileLoader(false);
        dispatch(setMobileMenuOpen(true));
        setHasShownInitialMobileMenu(true);
      }, 2000); // Increased delay to show loader longer

      return () => clearTimeout(timer);
    }
  }, [isMobile, auth, hasShownInitialMobileMenu, account, dispatch]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    if (isLayoutMode) return; // Disable menu interactions in layout mode
    if (isMobile) {
      setAnchorEl(userOptionRef?.current);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleClose = useCallback(() => {
    if (isLayoutMode) return; // Disable menu interactions in layout mode
    setAnchorEl(null);
  }, [isLayoutMode]);

  // Close menu when clicking outside or pressing Escape (especially important for mobile)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && !event.target.closest('.MuiMenu-root') && !event.target.closest('[aria-haspopup="true"]')) {
        handleClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (open && event.key === 'Escape') {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [handleClose, open]);



  useEffect(() => {
    const updateMenuState = () => {
      const root = document.querySelector(":root");
      if (window.innerWidth > LAPTOP_RESOLUTION) {
        if (!isLayoutMode) {
          root.style.setProperty("--sidebar-width", EXPAND_WIDTH);
        }
        dispatch(setExpandWidth(EXPAND_WIDTH));
        // expanded is derived from expandWidth
      } else {
        if (isMobile) {
          root.style.setProperty("--sidebar-width", "0px");
        } else if (!isLayoutMode) {
          root.style.setProperty("--sidebar-width", NOT_EXPAND_WIDTH);
        }
        dispatch(setExpandWidth(NOT_EXPAND_WIDTH));
      }
    };
    // Set the initial state
    updateMenuState();
    // Add event listener to update state on resize
    window.addEventListener('resize', updateMenuState);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', updateMenuState);
  }, [EXPAND_WIDTH, dispatch, isMobile, isLayoutMode]);

  useEffect(() => {
    async function fetchMyAPI() {
      const res = await getShortcutSetting();
      if (res && res.data) {
        dispatch(setSelectedShortcutOptions(res.data));
      }
    }
    fetchMyAPI()
  }, [dispatch])

  const checkMenuPermission = useCallback(
    (roles) => {
      const { role = null } = auth;
      if (role && role.slug) {
        if (_.includes(roles, role.slug)) {
          return true;
        }
      }
      return false;
    },
    [auth]
  );

  const handleSignOut = () => {
    signOut(navigate, () => {
      timeLogControl?.stopLogging?.();
    });
  };
  const { setPreviousParams } = useSetParamToURL();
  const suMenuItem = useMemo(() => {
    const dashboardAccess =
      selectedAccount &&
        selectedAccount.dashboardAccess &&
        selectedAccount.dashboardAccess.length > 0
        ? selectedAccount.dashboardAccess
        : [];
    const items = [];

    const isActive = dashboardAccess.length === 1 ? true : false;

    if (_.includes(dashboardAccess, PAGE_TYPE.HOSPITAL)) {
      items.push({
        label: "Hospital",
        isActive,
        to: setPreviousParams("/dashboard/hospital"),
        icon: <HospitalSubSvg />,
        replace: true
      });
    }

    if (_.includes(dashboardAccess, PAGE_TYPE.COMMUNITY_TRANSFER)) {
      items.push({
        label: "Community Transfer",
        to: setPreviousParams("/dashboard/community-transfer"),
        icon: <CommunityTransferSubSvg />,
        onClick: () => {
          trackFeatureUsage(TUTORIAL_FEATURES.COMMUNITY_TRANSFERS).catch((error) => {
            console.error('Failed to track community transfers usage:', error);
          });
          triggerTutorial(TUTORIAL_FEATURES.COMMUNITY_TRANSFERS).catch((error) => {
            console.error('Failed to trigger community transfers tutorial:', error);
          });
        },
      });
    }

    if (_.includes(dashboardAccess, PAGE_TYPE.DECEASED)) {
      items.push({
        label: "Deceased",
        to: setPreviousParams("/dashboard/deceased"),
        icon: <DeceasedSubSvg />,
        onClick: () => {
          trackFeatureUsage(TUTORIAL_FEATURES.DECEASED_TRANSFERS).catch((error) => {
            console.error('Failed to track deceased transfers usage:', error);
          });
          triggerTutorial(TUTORIAL_FEATURES.DECEASED_TRANSFERS).catch((error) => {
            console.error('Failed to trigger deceased transfers tutorial:', error);
          });
        },
      });
    }

    if (_.includes(dashboardAccess, PAGE_TYPE.ADMISSION)) {
      items.push({
        label: "Admissions",
        to: setPreviousParams("/dashboard/admissions"),
        icon: <AdmissionsSubSvg />,
        onClick: () => {
          trackFeatureUsage(TUTORIAL_FEATURES.ADMISSIONS).catch((error) => {
            console.error('Failed to track admissions usage:', error);
          });
          triggerTutorial(TUTORIAL_FEATURES.ADMISSIONS).catch((error) => {
            console.error('Failed to trigger admissions tutorial:', error);
          });
        },
      });
    }
    if (_.includes(dashboardAccess, PAGE_TYPE.OVERALL)) {
      items.push({
        label: "Overall",
        isActive: !isActive,
        to: setPreviousParams("/dashboard/overall"),
        icon: <OverallSubSvg />,
        onClick: () => {
          trackFeatureUsage(TUTORIAL_FEATURES.OVERALL).catch((error) => {
            console.error('Failed to track overall usage:', error);
          });
          triggerTutorial(TUTORIAL_FEATURES.OVERALL).catch((error) => {
            console.error('Failed to trigger overall tutorial:', error);
          });
        },
      });
    }

    return items;
  }, [selectedAccount, setPreviousParams, trackFeatureUsage, triggerTutorial]);

  const getAllPercentageAgainst = () => {
    getPercentageAgainstAll().then(async (res) => {
      if (res.length > 0) {
        let resUpdated = res.map((item, index) => {
          return { ...item, index };
        });
        dispatch(setCustomePercentages(resUpdated));
      } else {
        dispatch(setCustomePercentages([]));
      }
    });
  };

  const getAllCustomCardFilters = () => {
    getCustomeCardFilters().then(async (res) => {
      // We always display all cards for all users.
      // Selection logic is now handled dynamically on the dashboard pages,
      // while only layout/category states are persisted in the database.
      dispatch(setDeceasedCards([]));
      dispatch(setCommunityTransferCards([]));
      dispatch(setHospitalCards([]));
      dispatch(setAdmissionCards([]));
      dispatch(setOverallCards([]));
    });
  };

  useEffect(() => {
    if (selectedAccount) {
      getAllPercentageAgainst();
      getAllCustomCardFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);



  const accountRef = useRef();
  const [accountAnchorEl, setAccountAnchorEl] = useState(null);

  const facilityRef = useRef();
  const [facilityAnchorEl, setFacilityAnchorEl] = useState(null);

  // Mobile Header Component
  const MobileHeader = () => (
    <AppBar
      position="fixed"
      sx={{
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        backgroundColor: 'background.paper',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        display: { xs: 'flex', md: 'none' }
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: 2, minHeight: '57px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TempLogo isMobile={true} />
        </Box>
        <IconButton
          onClick={() => dispatch(setMobileMenuOpen(true))}
          sx={{ color: 'primary.main' }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  // Mobile Loader Component
  const MobileLoader = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        color: '#1976d2'
      }}
    >
      <CircularProgress
        size={40}
        sx={{
          color: '#1976d2',
          mb: 2
        }}
      />
      <Typography
        variant="h6"
        sx={{
          fontWeight: 500,
          textAlign: 'center',
          color: '#1976d2'
        }}
      >
        Loading...
      </Typography>
    </Box>
  );

  // If mobile, render the mobile header and MobileApp component
  if (isMobile) {
    return (
      <>
        {showMobileLoader ? (
          <MobileLoader />
        ) : (
          <>
            <MobileHeader />
            <MobileApp
              open={mobileMenuOpen}
              onClose={() => dispatch(setMobileMenuOpen(false))}
            />
          </>
        )}
      </>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div
        className={`df aic ${styles.hdr} ${expanded ? styles.expandedSidebar : ""}`}
        style={{
          width: isLayoutMode ? NOT_EXPAND_WIDTH : expandWidth,
          overflowY: isLayoutMode ? 'hidden' : expanded ? 'auto' : 'hidden',
          overflowX: 'hidden',
          display: { xs: 'none', md: 'flex' },
          alignItems: expanded ? 'stretch' : 'center'
        }}
      >
        <div
          className={`df aic ${styles.logoWrpr}`}
          style={{
            justifyContent: !expanded ? "center" : "space-between",
            width: isLayoutMode ? NOT_EXPAND_WIDTH : expandWidth,
          }}
        >
          {expanded && <TempLogo isMobile={false} />}
          <IconButton
            sx={{
              height: "35px",
              width: "35px",
              pointerEvents: isLayoutMode ? 'none' : 'auto',
              opacity: isLayoutMode ? 0.5 : 1
            }}
            onClick={() => {
              if (isLayoutMode) return; // Disable expand/collapse in layout mode
              const root = document.querySelector(":root");
              if (!expanded) {
                root.style.setProperty("--sidebar-width", EXPAND_WIDTH);
                dispatch(setExpandWidth(EXPAND_WIDTH));
              } else {
                root.style.setProperty("--sidebar-width", NOT_EXPAND_WIDTH);
                dispatch(setExpandWidth(NOT_EXPAND_WIDTH));
              }
            }}
          >
            <div
              style={{
                transform: !expanded ? "rotate(180deg)" : "",
                transition: "width 0s ease-in",
                display: "flex",
                alignItems: "center",
              }}
            >
              <ExpandArrowSvg />
            </div>
          </IconButton>
        </div>

        <div className={`${styles.sidebarContentColumn} ${styles.selectionsWrpr} ${styles.linksWrpr}`}>
          <div
            className={!expanded ? styles.withoutExpandSelection : styles.accountSection}
            onClick={() => {
              if (isLayoutMode || accountAnchorEl) return; // Disable in layout mode
              setAccountAnchorEl(accountRef?.current);
            }}
            style={{
              pointerEvents: isLayoutMode ? 'none' : 'auto',
              opacity: isLayoutMode ? 0.5 : 1
            }}
          >
            <div className={`${styles.subHeader} ${!expanded ? styles.subHeaderCollapsed : ""}`}>
              ACCOUNT
            </div>
            <AccountSelection
              expanded={expanded}
              account={account}
              accountRef={accountRef}
              setAccountAnchorEl={setAccountAnchorEl}
              accountAnchorEl={accountAnchorEl}
              isMobile={isMobile}
            />
          </div>
          <div
            onClick={() => {
              if (isLayoutMode || facilityAnchorEl) return; // Disable in layout mode
              setFacilityAnchorEl(facilityRef?.current);
            }}
            className={`${expanded ? styles.facilitySection : ""} ${!expanded ? styles.withoutExpandSelection : ""} ${!expanded ? styles.withoutExpandSelectionFacility : ""}`}
            style={{
              pointerEvents: isLayoutMode ? 'none' : 'auto',
              opacity: isLayoutMode ? 0.5 : 1
            }}>
            <div className={`${styles.subHeader} ${!expanded ? styles.subHeaderCollapsed : ""}`}>
              FACILITIES
            </div>
            <FacilitySelection
              expanded={expanded}
              selectedFacility={selectedFacilities}
              facilityRef={facilityRef}
              facilityAnchorEl={facilityAnchorEl}
              setFacilityAnchorEl={setFacilityAnchorEl}
              selectedAccount={selectedAccount}
              isMobile={isMobile}
            />
          </div>
        </div>

        <div className={`df ffc ffmar fw5400 fs14 ${styles.sidebarContentColumn} ${styles.scrollableContent}`}>
          <div
            className={`${styles.subHeader} ${styles.mainMenuTitle} ${styles.sectionHeaderWithDot} ${!expanded ? styles.subHeaderCollapsed : ""
              }`}
          >
            <span>Resident Records</span>
            {expanded && isMainMenuActive && <div className={styles.dashboardDot} />}
          </div>
          {permission && permission.role === AUTH_ROLES.USER
            ? permission.facilityAccess.write && (
              <NavWrpr
                strict
                to={setPreviousParams("/data-input")}
                label={"Data Input"}
                expanded={expanded}
                icon={<DataInputNavSVG />}
                key={`nav-Data Input`}
                disabled={isLayoutMode}
                isMobile={isMobile}
              />
            )
            : (
              <NavWrpr
                strict
                to={setPreviousParams("/data-input")}
                label={"Data Input"}
                expanded={expanded}
                key={`nav-Data Input`}
                icon={<DataInputNavSVG />}
                disabled={isLayoutMode}
                isMobile={isMobile}
              />
            )}
          <NavWrpr
            strict
            to={setPreviousParams("/residence-list")}
            label={"Residents List"}
            expanded={expanded}
            icon={<PeopleIcon />}
            key={`nav-Residents List`}
            disabled={isLayoutMode}
            isMobile={isMobile}
          />

          <div
            className={`${styles.subHeader} ${styles.dashboardHeaderWrpr} ${!expanded ? styles.subHeaderCollapsed : ""
              }`}
          >
            <span>DASHBOARD</span>
            {expanded && isDashboardActive && <div className={styles.dashboardDot} />}
          </div>
          {
            permission && permission.role === AUTH_ROLES.USER
              ? permission.facilityAccess.read &&
              suMenuItem.map((ele) => (
                <NavWrpr
                  icon={ele?.icon}
                  to={ele?.to}
                  label={ele?.label}
                  strict
                  expanded={expanded}
                  replace={ele?.replace}
                  key={`nav-${ele?.label}`}
                  disabled={isLayoutMode}
                  isMobile={isMobile}
                  onClick={ele?.onClick}
                />
              ))
              : suMenuItem.map((ele) => (
                <NavWrpr
                  icon={ele?.icon}
                  to={ele?.to}
                  label={ele?.label}
                  replace={ele?.replace}
                  expanded={expanded}
                  strict
                  key={`nav-${ele?.label}`}
                  disabled={isLayoutMode}
                  isMobile={isMobile}
                  onClick={ele?.onClick}
                />
              ))
          }
        </div>

        <div className={`df ffc mta ffmm ${styles.usernameWrpr}`}>
          <div
            className={`df aic ${!expanded ? styles.usernameNotExpand : null} ${styles.username
              }`}
            ref={userOptionRef}
          >
            <Box
              component="div"
              className={styles.usernameInnerSection}
              onClick={handleClick}
              aria-controls={open ? "account-settings-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              style={{
                pointerEvents: isLayoutMode ? 'none' : 'auto',
                opacity: isLayoutMode ? 0.5 : 1
              }}
            >
              <div className={`${styles.userInfoWrapper} ${!expanded ? styles.expandedUserInfoWrapper : null}`}>
                <div className={styles.avatarWrpr}>
                  <Avatar
                    src={`/assets/avatars/${auth.avatar}.png`}
                    alt={auth.fullName}
                    className={`m-r-8`}
                  />
                  <div className={styles.onlineDot}></div>
                </div>
                {expanded && (
                  <div className={styles.userNameRole}>
                    <p className={`${styles.roleName} ffmar fw500`}>{auth.fullName}</p>
                    <div className={styles.role}>{roles[auth.type]}</div>
                  </div>
                )}
              </div>
              {expanded && (
                <span
                  className={classNames(styles.logoutIconBtn)}
                >
                  <LogoutIcon sx={{ fontSize: 18 }} />
                </span>)}
            </Box>
          </div>
          {showAddAccount ? (
            <AddAccount close={() => setShowAddAccount(false)} />
          ) : undefined}
        </div>

        {/* Desktop Profile Menu */}
        {!isMobile && (
          <ProfileMenu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onSignOut={handleSignOut}
            isMobile={false}
            checkMenuPermission={checkMenuPermission}
            setPreviousParams={setPreviousParams}
            showAllOptions={true}
            auth={auth}
            roles={roles}
          />
        )}
      </div>
    </>
  );
};

export default Header;
